import { Dayjs } from 'dayjs'
import { request } from '../request'
import { Model, ModelID } from '@models/Model'
import { Variable } from '@models/Variable'
import { Level } from '@models/Level'
import api from '..'
import { useMeteoVariablesStore } from 'src/stores/meteoVariables'
import { ApiModule } from '@enums/ApiModules'
import { symbolModelsCompatibleMock } from 'src/__tests__/mocks/SymbolMap.mock'
import { IconModelDTO } from '@models/api/IconModelDTO'

export const variables = {
  getVariables: function () {
    return request.get('variables/fgb')
  },

  getNewVariables: function () {
    return request.get('variables/timeGapsVars')
  },

  getVariableRequest: function (
    module: ApiModule,
    model: string,
    variable: string,
    datetime: Dayjs,
    level?: string,
    dateOut?: Dayjs,
    simplify = false,
    startDatetime?: Dayjs
  ) {
    const params: AxiosParams = {
      module,
      model,
      dateTime: datetime.utc().format('YYYY-MM-DDTHH:mm:ss'),
      variable,
      smoothed: simplify
    }

    if (dateOut) {
      params.modelDate = dateOut.utc().format('YYYY-MM-DD')

      if (level) {
        params.level = level
        params.output = dateOut.utc().format('HH')
      }
    }

    if (startDatetime) {
      params.dateInit = startDatetime.utc().format('YYYY-MM-DDTHH:mm:ss')
    }

    return {
      url: `${request.defaults.baseURL}/variables/getVariableFile`,
      params
    }
  },

  getMeteoVariableURL: function (
    model: Model,
    variable: Variable,
    level: Level,
    date: Dayjs,
    outDate: Dayjs,
    isMosaic = false,
    simplify = false
  ) {
    const module = ApiModule.METEO

    if (useMeteoVariablesStore().isHistoricalOutActive && !isMosaic) {
      return api.historicalMeteo.getMapRequest(
        module,
        model.id,
        level.variableLoadId,
        date,
        outDate,
        level.reference,
        simplify
      )
    } else {
      if (variable.isRadar()) {
        // TODO: caso especial del radar
        return this.getVariableRequest(
          module,
          variable.reference,
          level.reference,
          date
        )
      }

      return this.getVariableRequest(
        module,
        model.id,
        level.variableLoadId,
        date,
        level.reference,
        outDate ?? level.outDate ?? model.outDate,
        simplify
      )
    }
  },

  getStreamlinesURL: function (
    model: Model,
    variable: Variable,
    level: Level,
    dateTime: Dayjs,
    selectedModelDate?: Dayjs
  ) {
    const isHistoric =
      selectedModelDate && selectedModelDate.isBefore(model.outDate)

    const module = ApiModule.METEO
    if (isHistoric) {
      return api.historicalMeteo.getMapRequest(
        module,
        variable.reference,
        level.reference,
        dateTime,
        selectedModelDate
      )
    } else {
      return this.getVariableRequest(
        module,
        variable.reference,
        level.reference,
        dateTime
      )
    }
  },

  getIsobarsURL: function (modelId: ModelID, datetime: Dayjs) {
    const model = useMeteoVariablesStore().modelById(modelId)!
    const selectedOut = useMeteoVariablesStore().selectedOut
    const isobarsVariable = model.getVariable('ISOB')
    if (isobarsVariable) {
      const level = isobarsVariable.levels[0]
      const isHistoric = selectedOut && selectedOut.isBefore(model.outDate)
      let url: string

      const module = ApiModule.METEO
      if (isHistoric) {
        return api.historicalMeteo.getMapRequest(
          module,
          model.id,
          level.variableLoadId,
          datetime,
          selectedOut
        )
      } else {
        return this.getVariableRequest(
          module,
          model.id,
          level.variableLoadId,
          datetime,
          level.reference,
          model.outDate
        )
      }
    }
  },

  getFireParams: function () {
    return request.get('variables/incendiosVars')
  },

  downloadModelURL: function (model: Model) {
    const idModel = model.id
    const datetime = model.getDate()

    const modelDate = datetime.utc().format('YYYY-MM-DD')
    const out = datetime.utc().format('HH')
    const url = `variables/dowloadModel?model=${idModel}&modelDate=${modelDate}&output=${out}`

    // return request.get(url, { responseType: 'blob' })
    return `${request.defaults.baseURL}/${url}`
  },

  getVariableTiff: function (
    module: ApiModule,
    model: string,
    variable: string,
    level?: string,
    dateOut?: Dayjs
  ) {
    const url = `${request.defaults.baseURL}/variables/getVariableTif?module=${module}&model=${model}&variable=${variable}`
    let optionals = ''
    if (dateOut) {
      const dateOutUTC = dateOut.utc().format('YYYY-MM-DD')
      optionals += `&modelDate=${dateOutUTC}`
    }
    if (level && dateOut) {
      optionals += `&level=${level}&output=${dateOut.utc().format('HH')}`
    }

    return `${url}${optionals}`
  },

  getVariableMedia: function (
    hasCoastline: boolean | undefined,
    hasAdministrativeBoundaries: boolean | undefined,
    idLevel: Level['id'],
    modelDate: string | undefined,
    salida: string | undefined,
    tipoArchivo: string | undefined
  ) {
    const url = `${request.defaults.baseURL}/media?hasCoastLine=${hasCoastline}&hasAdministrativeBoundaries=${hasAdministrativeBoundaries}&idNivel=${idLevel}&modelDate=${modelDate}&salida=${salida}&tipoArchivo=${tipoArchivo}`
    return `${url}`
  },

  getWeatherIconsModels: function () {
    return request.get<IconModelDTO[]>('variables/getWeatherIconsModels')
  }
}
